import { ONE_MINUTE_IN_MS } from '@careerstart/wae-common/src/main/constants/timeConversions';

import { getTimeZone } from '../../../../utils';
import { getSundayOfWeek } from '../../../../utils/timeConverter';

export const MINUTES_IN_DAY = 24 * 60;
export const MAX_SHIFT_DURATION_MINUTES = 20 * 60;

// Re-export to keep existing imports stable (single source of truth lives in `src/main/utils/timeConverter.js`)
export { getSundayOfWeek };

const isShiftDurationInvalid = (
  shift,
  { minutesInDay = MINUTES_IN_DAY, maxShiftDurationMinutes = MAX_SHIFT_DURATION_MINUTES } = {}
) => {
  if (!shift || shift.startTime == null || shift.endTime == null) return false;
  const { startTime, endTime } = shift;
  const duration = endTime >= startTime ? endTime - startTime : minutesInDay - startTime + endTime;
  return duration === 0 || duration > maxShiftDurationMinutes;
};

export const computeHasInvalidShiftDuration = (
  week1Shifts,
  week2Shifts,
  { minutesInDay = MINUTES_IN_DAY, maxShiftDurationMinutes = MAX_SHIFT_DURATION_MINUTES } = {}
) =>
  Object.values(week1Shifts || {}).some((s) =>
    isShiftDurationInvalid(s, { minutesInDay, maxShiftDurationMinutes })
  ) ||
  Object.values(week2Shifts || {}).some((s) =>
    isShiftDurationInvalid(s, { minutesInDay, maxShiftDurationMinutes })
  );

/**
 * Build a 2-week schedule *pattern* (Week 1 + Week 2 only).
 * This is used for the review modal preview to show the repeating weekly pattern,
 * independent of the full job duration date range.
 */
export const buildSchedulePatternWeeks = ({
  week1Shifts,
  week2Shifts,
  daysOfWeekFull,
  minutesToReadableTime,
}) => {
  const toRows = (shiftsMap) =>
    Object.keys(shiftsMap || {})
      .map((dayIndexStr) => {
        const dayIndex = parseInt(dayIndexStr, 10);
        const shift = shiftsMap?.[dayIndexStr];
        if (!shift) return null;
        if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return null;
        if (shift.startTime == null || shift.endTime == null) return null;

        const openings = shift.openings ?? 1;
        const startTimeStr = minutesToReadableTime(shift.startTime);
        const endTimeStr = minutesToReadableTime(shift.endTime);

        return {
          dayIndex,
          dayName: daysOfWeekFull[dayIndex],
          timeRange: `${startTimeStr} — ${endTimeStr}`,
          openings,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.dayIndex - b.dayIndex);

  const week1Rows = toRows(week1Shifts);
  const week2Rows = toRows(week2Shifts);

  return [
    {
      weekNumber: 1,
      rows: week1Rows,
      totalOpenings: week1Rows.reduce((sum, r) => sum + (r.openings || 0), 0),
    },
    {
      weekNumber: 2,
      rows: week2Rows,
      totalOpenings: week2Rows.reduce((sum, r) => sum + (r.openings || 0), 0),
    },
  ];
};

export const buildRecurringTwoWeeksApiPayload = ({
  generatedDates,
  positionTemplateId,
  departmentId,
  locationId,
  startDate,
  endDate,
  week1Shifts,
  week2Shifts,
}) => {
  const startDateEpoch = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
  const endDateEpoch = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;

  const jobOrders = (generatedDates || [])
    .filter(
      // Filter out shifts with null or invalid times as a safety measure
      (dateInfo) =>
        dateInfo.startTimeMinutes != null &&
        dateInfo.endTimeMinutes != null &&
        typeof dateInfo.startTimeMinutes === 'number' &&
        typeof dateInfo.endTimeMinutes === 'number'
    )
    .map((dateInfo) => {
      // Calculate start and end times in epoch milliseconds
      const start = new Date(dateInfo.date);
      start.setHours(
        Math.floor(dateInfo.startTimeMinutes / 60),
        dateInfo.startTimeMinutes % 60,
        0,
        0
      );

      const end = new Date(dateInfo.date);
      end.setHours(Math.floor(dateInfo.endTimeMinutes / 60), dateInfo.endTimeMinutes % 60, 0, 0);

      // If end time is before start time, it's an overnight shift
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }

      return {
        start: start.getTime(),
        end: end.getTime(),
        positionTemplateId,
        departmentId,
        locationId,
        numOpenings: dateInfo.openings,
      };
    });

  const toOccurrence = (offsetDay, shift) => {
    if (!shift || shift.startTime == null || shift.endTime == null) return null;
    if (typeof shift.startTime !== 'number' || typeof shift.endTime !== 'number') return null;

    const openings = shift.openings ?? 1;
    const numOpenings = parseInt(openings, 10);
    if (!numOpenings || numOpenings < 1) return null;

    return {
      offsetDay,
      shifts: [
        {
          startMinutes: shift.startTime,
          endMinutes: shift.endTime,
          numOpenings,
        },
      ],
    };
  };

  const occurrences = [
    ...Object.keys(week1Shifts || {}).map((dayIndexStr) => {
      const dayIndex = parseInt(dayIndexStr, 10);
      if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return null;
      return toOccurrence(dayIndex, week1Shifts?.[dayIndexStr]);
    }),
    ...Object.keys(week2Shifts || {}).map((dayIndexStr) => {
      const dayIndex = parseInt(dayIndexStr, 10);
      if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return null;
      return toOccurrence(dayIndex + 7, week2Shifts?.[dayIndexStr]);
    }),
  ]
    .filter(Boolean)
    .sort((a, b) => a.offsetDay - b.offsetDay);

  const maxEndOffsetMinutes = occurrences.reduce((maxMinutes, occ) => {
    const shift = occ?.shifts?.[0];
    if (!shift) return maxMinutes;

    const { startMinutes, endMinutes } = shift;
    const overnight = startMinutes > endMinutes;
    const endOffset = overnight ? MINUTES_IN_DAY + endMinutes : endMinutes;
    return Math.max(maxMinutes, endOffset);
  }, 0);

  const pattern =
    startDateEpoch != null && endDateEpoch != null && occurrences.length > 0
      ? {
          timeZone: getTimeZone(),
          cycle: {
            lengthDays: 14,
            anchorDate: getSundayOfWeek(new Date(startDateEpoch)).getTime(),
          },
          occurrences,
          jobOrderDefaults: {
            positionTemplateId,
            departmentId,
            locationId,
          },
          scheduleWindow: {
            start: startDateEpoch,
            end: endDateEpoch + maxEndOffsetMinutes * ONE_MINUTE_IN_MS,
          },
        }
      : undefined;

  return pattern ? { jobOrders, pattern } : { jobOrders };
};
